import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import { useProjects } from "../../common/contexts/ProjectContext";
import { slugify } from "../../../utils/slugify";
import { LoadingScreen } from "../../common/components/LoadingScreen";
import appLogo from "../../../assets/app-logo.png";

// Maps each role to the module URL it should land on after login.
const ROLE_HOME_PATH: Record<string, string> = {
  Logger: "/logger",
  "Story Producer": "/storyproducer",
  Showrunner: "/showrunner",
  "Content Controller": "/controller",
  "Content Director": "/contentdirector",
  Admin: "/admin",
  Executive: "/executive",
  Editor: "/editor",
  Cliente: "/cliente",
  Superadmin: "/superadmin",
};

export const ModuleSelector: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { loginWithGoogle, login, user, resetPassword } = useAuth();
  const { activeProject } = useProjects();

  const isAuthenticated = !!user;

  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);
  const [successMsg, setSuccessMsg] = React.useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = React.useState(false);

  const [oauthError, setOauthError] = React.useState<string | null>(() => {
    const err = localStorage.getItem("auth_error");
    if (err) {
      localStorage.removeItem("auth_error");
      return err;
    }
    return null;
  });

  const projectSlug = activeProject ? slugify(activeProject.name) : "";

  // Each role lands directly on its own module after login.
  React.useEffect(() => {
    if (!isAuthenticated || !user || !activeProject) return;
    const path = ROLE_HOME_PATH[user.role];
    if (path) {
      navigate(`/${projectSlug}${path}`, { replace: true });
    }
  }, [isAuthenticated, user, activeProject, projectSlug, navigate]);

  // Authenticated users are on their way to their role module — don't flash the login UI.
  if (isAuthenticated && activeProject) {
    const hasValidRole = user && !!ROLE_HOME_PATH[user.role];
    if (hasValidRole) {
      return <LoadingScreen />;
    }

    // Fallback if role is unrecognized or invalid for the project
    return (
      <div
        style={{
          height: "100vh",
          width: "100vw",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          background: "#000",
          fontFamily: "var(--font-family)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            background: "rgba(30, 30, 30, 0.95)",
            padding: "40px",
            borderRadius: "12px",
            border: "1px solid #ff4444",
            width: "400px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
            textAlign: "center",
          }}
        >
          <h2 style={{ color: "#ff4444", margin: 0 }}>Acceso Denegado</h2>
          <p style={{ color: "var(--color-text-primary)" }}>
            El rol asignado a tu cuenta (<strong>{user?.role}</strong>) no es válido o no tiene
            módulos asignados en este proyecto.
          </p>
          <button
            onClick={() => navigate("/projects")}
            style={{
              marginTop: "10px",
              background: "var(--color-accent-primary)",
              color: "#fff",
              border: "none",
              padding: "12px",
              borderRadius: "6px",
              fontSize: "0.95rem",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Volver a Proyectos
          </button>
        </div>
      </div>
    );
  }

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMsg(null);
    setOauthError(null);
    setIsLoggingIn(true);
    const res = await login(email, password);
    setIsLoggingIn(false);
    if (!res.success) {
      setError(res.error || "Login failed");
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError("Por favor ingresa tu correo primero para recuperar la contraseña.");
      return;
    }
    setError(null);
    setSuccessMsg(null);
    setOauthError(null);
    setIsLoggingIn(true);
    const res = await resetPassword(email);
    setIsLoggingIn(false);
    if (res.success) {
      setSuccessMsg("Te hemos enviado un enlace para restablecer tu contraseña.");
    } else {
      setError(res.error || "Reset failed");
    }
  };

  const handleGoogleLogin = async () => {
    setError(null);
    setOauthError(null);
    setIsLoggingIn(true);
    const res = await loginWithGoogle();
    if (!res.success) {
      setIsLoggingIn(false);
      setError(res.error || "Google login failed");
    }
  };

  return (
    <div
      style={{
        height: "100vh",
        width: "100vw",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        background: "#000",
        fontFamily: "var(--font-family)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          textAlign: "center",
          marginBottom: "40px",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          zIndex: 10,
        }}
      >
        <img src={appLogo} alt="Braindog" style={{ height: "110px", marginBottom: "30px" }} />
        <div
          style={{
            height: "80px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
            width: "100%",
          }}
        >
          <h1
            style={{
              fontSize: "2rem",
              letterSpacing: "4px",
              margin: 0,
              color: "var(--color-accent-primary)",
              textTransform: "uppercase",
            }}
          >
            {isAuthenticated ? t("app.selectRole") : t("app.signIn")}
          </h1>
          <p
            style={{
              color: "var(--color-text-muted)",
              margin: 0,
              fontSize: "1rem",
              opacity: 0.8,
              marginTop: "10px",
            }}
          >
            {isAuthenticated ? t("app.selectOperationalRole") : t("app.signInSubtitle")}
          </p>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <div
          style={{
            background: "rgba(30, 30, 30, 0.95)",
            padding: "40px",
            borderRadius: "12px",
            border: "1px solid var(--color-panel-border)",
            width: "400px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.5)",
            display: "flex",
            flexDirection: "column",
            gap: "20px",
          }}
        >
          <h3
            style={{
              margin: 0,
              color: "var(--color-text-primary)",
              textAlign: "center",
              marginBottom: "5px",
              letterSpacing: "2px",
              fontSize: "1rem",
            }}
          >
            {t("auth.login.secureLogin")}
          </h3>

          {(error || oauthError) && (
            <div
              style={{
                color: "#ff4444",
                background: "rgba(255,0,0,0.1)",
                padding: "10px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              {error || oauthError}
            </div>
          )}

          {successMsg && (
            <div
              style={{
                color: "#4ade80",
                background: "rgba(74, 222, 128, 0.1)",
                padding: "10px",
                borderRadius: "8px",
                fontSize: "0.85rem",
                textAlign: "center",
              }}
            >
              {successMsg}
            </div>
          )}

          {/* Google OAuth Button - Temporarily commented out
          <button
            onClick={handleGoogleLogin}
            disabled={isLoggingIn}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "12px",
              background: "#fff",
              color: "#333",
              border: "none",
              padding: "14px",
              borderRadius: "8px",
              fontSize: "0.95rem",
              fontWeight: 700,
              cursor: isLoggingIn ? "wait" : "pointer",
              transition: "all 0.2s ease",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.3)";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.2)";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            <svg width="20" height="20" viewBox="0 0 48 48">
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            {t("auth.login.continueWithGoogle")}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: "15px" }}>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "rgba(255,255,255,0.1)",
              }}
            ></div>
            <span
              style={{ color: "#555", fontSize: "0.75rem", fontWeight: "bold" }}
            >
              {t("auth.login.or")}
            </span>
            <div
              style={{
                flex: 1,
                height: "1px",
                background: "rgba(255,255,255,0.1)",
              }}
            ></div>
          </div>
          */}

          <form
            onSubmit={handleLogin}
            style={{ display: "flex", flexDirection: "column", gap: "15px" }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label
                style={{
                  color: "var(--color-text-muted)",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                }}
              >
                {t("auth.login.email")}
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                style={{
                  background: "#111",
                  border: "1px solid #333",
                  padding: "12px",
                  borderRadius: "6px",
                  color: "#fff",
                  outline: "none",
                  fontSize: "0.95rem",
                }}
                placeholder={t("auth.login.emailPlaceholder")}
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
              <label
                style={{
                  color: "var(--color-text-muted)",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  letterSpacing: "0.5px",
                }}
              >
                {t("auth.login.password")}
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                style={{
                  background: "#111",
                  border: "1px solid #333",
                  padding: "12px",
                  borderRadius: "6px",
                  color: "#fff",
                  outline: "none",
                  fontSize: "0.95rem",
                }}
                placeholder={t("auth.login.passwordPlaceholder")}
              />
            </div>
            <button
              type="submit"
              disabled={isLoggingIn}
              style={{
                marginTop: "5px",
                background: "var(--color-accent-primary)",
                color: "#fff",
                border: "none",
                padding: "14px",
                borderRadius: "6px",
                fontSize: "0.95rem",
                fontWeight: 700,
                cursor: isLoggingIn ? "wait" : "pointer",
                opacity: isLoggingIn ? 0.7 : 1,
                transition: "all 0.2s",
              }}
            >
              {isLoggingIn ? t("auth.login.authenticating") : t("auth.login.accessPlatform")}
            </button>
          </form>

          <div
            style={{
              textAlign: "center",
              marginTop: "5px",
            }}
          >
            <button
              type="button"
              onClick={handleResetPassword}
              style={{
                background: "none",
                border: "none",
                color: "var(--color-text-muted)",
                fontSize: "0.85rem",
                cursor: "pointer",
                padding: 0,
                textDecoration: "underline",
                transition: "color 0.2s",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.color = "var(--color-accent-primary)")}
              onMouseLeave={(e) => (e.currentTarget.style.color = "var(--color-text-muted)")}
            >
              ¿Olvidaste tu contraseña?
            </button>
          </div>
        </div>

        {/* Back to projects */}
        <button
          onClick={() => navigate("/projects")}
          style={{
            marginTop: "30px",
            background: "rgba(255,255,255,0.05)",
            border: "1px solid var(--color-panel-border)",
            color: "var(--color-text-muted)",
            padding: "10px 25px",
            cursor: "pointer",
            borderRadius: "var(--border-radius-pill)",
            fontSize: "0.8rem",
            fontWeight: "bold",
            transition: "all 0.3s ease",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = "var(--color-accent-primary)")}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = "var(--color-panel-border)")}
        >
          <i className="bx bx-arrow-back"></i> {t("auth.login.backToProjects")}
        </button>
      </div>
    </div>
  );
};
